<template>
  <div>
    <label
      :for="id"
      class="label mb-2"
      v-html="label"
    />

    <p
      v-if="help"
      :id="helpID"
      class="help mb-4"
    >
      {{ help }}
    </p>

    <c-message
      v-if="errors"
      type="error"
      class="errors mb-2"
    >
      <p
        v-for="(error, index) in errors"
        :id="errorsID"
        :key="index"
      >
        {{ error }}
      </p>
    </c-message>

    <input
      :id="id"
      ref="input"
      class="input"
      :aria-describedby="ariaDescribedBy"
      :aria-invalid="ariaInvalid"
      :value="displayValue"
      :disabled="!published"
      :required="$attrs.required"
      type="text"
      inputmode="decimal"
      @input="handleInput"
      @focus="handleFocus"
      @blur="handleBlur"
    >

    <p class="help mt-3">
      Format: X.XXX,XX (z.B. 5.000,00 oder 12.500,50)
    </p>
  </div>
</template>

<script>
import { nanoid } from 'nanoid'
import CMessage from './CMessage.vue'

export default {
  name: 'CFormCurrency',

  components: {
    CMessage
  },

  inheritAttrs: false,

  props: {
    value: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      required: true
    },
    help: {
      type: String,
      default: undefined
    },
    errors: {
      type: Array,
      default: undefined
    }
  },

  data () {
    return {
      displayValue: '',
      isFocused: false
    }
  },

  computed: {
    id: () => nanoid(),
    helpID () {
      return `${this.id}-help`
    },
    errorsID () {
      return `${this.id}-error`
    },
    ariaInvalid () {
      return this.errors ? 'true' : 'false'
    },
    ariaDescribedBy () {
      const ids = []

      if (this.help) {
        ids.push(this.helpID)
      }

      if (this.errors) {
        ids.push(this.errorsID)
      }

      if (ids.length) {
        return ids.join(' ')
      }

      return false
    },
    published () {
      return this.$store.state.publishedInfo.published
    }
  },

  watch: {
    value: {
      immediate: true,
      handler (newValue) {
        if (!this.isFocused) {
          this.displayValue = newValue ? this.formatForDisplay(newValue) : ''
        }
      }
    }
  },

  methods: {
    formatForDisplay (value) {
      if (!value) return ''

      let cleanValue = value.toString().replace(/[^\d,.-]/g, '')
      cleanValue = cleanValue.replace(/\./g, '').replace(',', '.')

      const num = parseFloat(cleanValue)

      if (isNaN(num)) return value

      return new Intl.NumberFormat('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(num)
    },

    handleInput (event) {
      const input = event.target.value
      const cleanInput = input.replace(/[^\d,.-]/g, '')

      this.displayValue = cleanInput
      this.$emit('input', cleanInput)
    },

    handleFocus (event) {
      this.isFocused = true
    },

    handleBlur (event) {
      this.isFocused = false

      const input = event.target.value

      if (!input || input.trim() === '') {
        this.displayValue = ''
        this.$emit('input', '')
        return
      }

      const formatted = this.formatForDisplay(input)
      this.displayValue = formatted
      this.$emit('input', formatted)
    }
  }
}
</script>

<style lang="scss" scoped>
@use "../css/variables.scss";

.label {
  display: inline-block;
  font-weight: 700;
}

.help {
  font-size: 0.875em;
  color: variables.$color-gray-700;
}

.input {
  position: relative;
  display: block;
  width: 100%;
  padding: 0.375em 0.5em;
  background: variables.$color-gray-100;
  border: 0.125em solid variables.$color-gray-400;
  border-radius: variables.$border-radius;
  outline: 0;
  transition: border-color 0.15s;

  &:focus {
    border-color: variables.$color-gray-900;
  }
}

.errors {
  font-size: 0.875em;
}
</style>
